import { useCallback, useState } from 'react';
import { getPlatformTermsContent, type PlatformTermsVariant } from '../constants/platformTermsContent';
import {
  getClientLegalDocuments,
  type ClientLegalDocumentId,
} from '../constants/clientLegalContent';
import { useI18n } from '../i18n';
import { ClientLegalDocumentBody } from './ClientLegalDocumentBody';
import {
  ClientLegalDocumentSubsections,
  createClosedClientLegalOpenState,
  type ClientLegalOpenState,
} from './ClientLegalDocumentSubsections';
import styles from './ProfileMenu.module.css';

type ProfileHubTermsPanelProps = {
  onEraseAccount?: () => void;
  eraseError?: string | null;
  termsVariant?: PlatformTermsVariant;
  /** When false, only render erase actions (legal docs live as top-level hub rows). */
  showLegalDocuments?: boolean;
};

export function ProfileHubTermsEraseActions({
  onEraseAccount,
  eraseError,
}: {
  onEraseAccount?: () => void;
  eraseError?: string | null;
}) {
  const { t } = useI18n();
  if (!onEraseAccount) {
    return null;
  }
  return (
    <div className={styles.hubTermsEraseBlock}>
      <button type="button" className={styles.hubRowActionBtnDanger} onClick={onEraseAccount}>
        {t('profile.hub.eraseAccount')}
      </button>
      {eraseError ? (
        <p className={styles.hubRowExpandError} role="alert">
          {eraseError}
        </p>
      ) : null}
    </div>
  );
}

export function ProfileHubLegalDocumentPanel({
  documentId,
  onEraseAccount,
  eraseError,
}: {
  documentId: ClientLegalDocumentId;
  onEraseAccount?: () => void;
  eraseError?: string | null;
}) {
  const { language } = useI18n();
  const document = getClientLegalDocuments(language).find((row) => row.id === documentId);
  if (!document) {
    return null;
  }
  return (
    <article className={styles.hubTermsPanel}>
      <ClientLegalDocumentBody document={document} />
      {documentId === 'termsOfUse' ? (
        <ProfileHubTermsEraseActions onEraseAccount={onEraseAccount} eraseError={eraseError} />
      ) : null}
    </article>
  );
}

export function ProfileHubTermsPanel({
  onEraseAccount,
  eraseError,
  termsVariant = 'default',
  showLegalDocuments = true,
}: ProfileHubTermsPanelProps) {
  const { language } = useI18n();
  const [openDocuments, setOpenDocuments] = useState<ClientLegalOpenState>(
    createClosedClientLegalOpenState,
  );

  const onToggleDocumentOpen = useCallback((documentId: ClientLegalDocumentId) => {
    setOpenDocuments((current) => ({
      ...current,
      [documentId]: !current[documentId],
    }));
  }, []);

  if (termsVariant === 'assetOwner') {
    const content = getPlatformTermsContent(language, 'assetOwner');
    return (
      <article className={styles.hubTermsPanel}>
        {showLegalDocuments ? (
          <>
            <h4 className={styles.hubTermsHeading}>{content.heading}</h4>
            <p className={styles.hubTermsIntro}>{content.intro}</p>
            <div className={styles.hubTermsSections}>
              {content.sections.map((section) => (
                <section key={section.title} className={styles.hubTermsSection}>
                  <h5 className={styles.hubTermsSectionTitle}>{section.title}</h5>
                  <p className={styles.hubTermsSectionBody}>{section.body}</p>
                </section>
              ))}
            </div>
          </>
        ) : null}
        <ProfileHubTermsEraseActions onEraseAccount={onEraseAccount} eraseError={eraseError} />
      </article>
    );
  }

  return (
    <article className={styles.hubTermsPanel}>
      {showLegalDocuments ? (
        <ClientLegalDocumentSubsections
          idPrefix="profile-terms"
          open={openDocuments}
          theme="profile"
          showGroupHeading
          onToggleOpen={onToggleDocumentOpen}
        />
      ) : null}
      <ProfileHubTermsEraseActions onEraseAccount={onEraseAccount} eraseError={eraseError} />
    </article>
  );
}
